/* eslint-disable local-rules/set-state-in-use-effect */
import {useTranslation} from '@aboutyou/i18n';
import {useOpenedStateManager} from '@aboutyou/opened-state-manager';
import {useQueryParams} from '@aboutyou/router';
import {useFullSession} from '@aboutyou/user';
import {useEffect, useRef, useState} from 'react';
import {useIsAuthenticated} from '../../../../features/Authentication/hooks/useIsAuthenticated';
import {CouponDeepLinkActivationProps} from '../../../../features/CouponDeepLinkActivation/CouponDeepLinkActivation';
import {
  ACTIVATION_COUPON_STATE_KEY,
  COUPON_LOGIN_QUERY_PARAM,
} from '../../BasketPageCouponLoginChecker/BasketPageCouponLoginChecker';
import {useApplyCouponToExistingBasket} from '../../hooks/useApplyCouponToExistingBasket';

const DELAY_BEFORE_TRANSITION_TO_NEXT_STATE = 2000;
const AUTO_CLOSE_TIMEOUT = 6000;

type CouponActivationResponse = Pick<
  CouponDeepLinkActivationProps,
  'status' | 'headline' | 'couponReduction'
>;

export const useActivateBasketCoupon = () => {
  const {t} = useTranslation();
  const {isOpen, cancel} = useOpenedStateManager(ACTIVATION_COUPON_STATE_KEY);
  const params = useQueryParams();
  const session = useFullSession();

  const promotionId = params.get(COUPON_LOGIN_QUERY_PARAM);
  const activatedPromotion = useRef<string>(undefined);
  const applyCouponToBasket = useApplyCouponToExistingBasket();

  const [couponActivationResponse, setCouponActivationResponse] =
    useState<CouponActivationResponse>({status: 'loading'});

  const isAuthenticated = useIsAuthenticated().isAuthenticated;

  const setNextState = (state: CouponActivationResponse) => {
    setTimeout(() => {
      setCouponActivationResponse(state);
    }, DELAY_BEFORE_TRANSITION_TO_NEXT_STATE);
  };

  const cancelTimeoutIdRef = useRef<NodeJS.Timeout>(undefined);

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback, local-rules/too-complex-useeffects
  useEffect(() => {
    if (
      !session ||
      !isAuthenticated ||
      !promotionId ||
      activatedPromotion.current === promotionId
    ) {
      return;
    }

    activatedPromotion.current = promotionId;
    applyCouponToBasket(promotionId, session)
      .then(response => {
        if (response.error) {
          setNextState({
            status: 'error',
            headline: response.error?.message || '',
          });
        } else {
          // eslint-disable-next-line local-rules/set-state-in-use-effect
          setNextState({
            status: 'success',
            headline: `🎉 ${t('Your coupon is applied')} 🎉`,
            couponReduction:
              response.totalPrice.totalPrice?.totalReduction?.coupon,
          });
        }
      })
      .catch(() => {
        setNextState({
          status: 'error',
          headline: t('Oops, we couldn’t apply your coupon. We’re sorry!'),
        });
      })
      .finally(() => {
        cancelTimeoutIdRef.current = setTimeout(() => {
          cancel();
        }, AUTO_CLOSE_TIMEOUT);
      });
  }, [promotionId, isAuthenticated, session]);

  useEffect(() => {
    if (!isOpen) {
      clearTimeout(cancelTimeoutIdRef.current);
    }
  }, [isOpen]);

  return {
    isOpen,
    cancel,
    couponActivationResponse,
  };
};
